import { z } from 'zod';

// --- Tree node layout overrides ---

export const treeNodeLayoutSchema = z.object({
  width: z.number().optional(),
  height: z.number().optional(),
  offsetX: z.number().optional(),
  offsetY: z.number().optional(),
});
export type TreeNodeLayout = z.infer<typeof treeNodeLayoutSchema>;

// --- Sub-items within a tree node ---

export const subItemConfigSchema = z.object({
  label: z.string(),
  metricKey: z.string(),
  classification: z.string(),
  colorClass: z.string(),
  parentMetricKey: z.string(),
  pctBaseMetricKey: z.string(),
  tooltip: z.string().optional(),
});
export type SubItemConfig = z.infer<typeof subItemConfigSchema>;

// --- Tree node config ---

export const treeNodeConfigSchema = z.object({
  id: z.string(),
  metricKey: z.string().optional(),
  metricKeys: z.array(z.string()).optional(),
  metricCombination: z.enum(['union', 'intersection']).optional(),
  label: z.string(),
  classification: z.string(),
  colorClass: z.string(),
  parentMetricKey: z.string().optional(),
  tooltip: z.string().optional(),
  layout: treeNodeLayoutSchema.optional(),
  complementary: z.boolean().optional(),
  subItems: z.array(subItemConfigSchema).optional(),
});
export type TreeNodeConfig = z.infer<typeof treeNodeConfigSchema>;

// --- Tree edge config ---

export const treeEdgeConfigSchema = z.object({
  source: z.string(),
  target: z.string(),
});
export type TreeEdgeConfig = z.infer<typeof treeEdgeConfigSchema>;

// --- KPI section ---

export const kpiCardConfigSchema = z.object({
  metricKey: z.string(),
  label: z.string(),
  format: z.enum(['number', 'percentage', 'duration']).optional(),
});
export type KpiCardConfig = z.infer<typeof kpiCardConfigSchema>;

export const kpiSectionConfigSchema = z.object({
  cards: z.array(kpiCardConfigSchema),
});
export type KpiSectionConfig = z.infer<typeof kpiSectionConfigSchema>;

// --- Funnel section ---

export const funnelStageConfigSchema = z.object({
  metricKey: z.string(),
  label: z.string(),
});
export type FunnelStageConfig = z.infer<typeof funnelStageConfigSchema>;

export const funnelSectionConfigSchema = z.object({
  stages: z.array(funnelStageConfigSchema),
});
export type FunnelSectionConfig = z.infer<typeof funnelSectionConfigSchema>;

// --- Dashboard config type ---

export const dashboardConfigTypeSchema = z.enum(['analytics', 'quality']);
export type DashboardConfigType = z.infer<typeof dashboardConfigTypeSchema>;

// --- Full dashboard config ---

export const dashboardConfigSchema = z.object({
  version: z.literal(1),
  metrics: z.array(z.string()),
  tree: z.object({
    nodes: z.array(treeNodeConfigSchema),
    edges: z.array(treeEdgeConfigSchema),
  }),
  kpi: kpiSectionConfigSchema.optional(),
  funnel: funnelSectionConfigSchema.optional(),
});
export type DashboardConfig = z.infer<typeof dashboardConfigSchema>;
